import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MUIDataTable, {
  MUIDataTableOptions,
  MUIDataTableBodyCell,
  MUIDataTableState,
  MUIDataTableColumnDef,
} from 'mui-datatables';
import { makeStyles } from '@mui/styles';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import castArray from 'lodash/castArray';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { CustomToolbarItem } from 'shared/types/customToolbar';
import { Loader } from 'ui';

import { theme } from 'theme';
import { Box, Button } from '@mui/material';
import CustomToolbar from './CustomToolbar/CustomToolbar';
import { DatatableStyledWrapper } from './Datatable.styled';

const useStyle = makeStyles({
  container: {
    position: 'relative',
  },
  toolbarWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2),
    alignItems: 'center',
    position: 'absolute',
    right: theme.spacing(0),
    top: theme.spacing(1),
    height: theme.spacing(8),
    zIndex: 1,
  },
  toolbarWrapperWithLeftMenu: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    padding: theme.spacing(0, 2, 0, 0),
    width: '100%',
    height: theme.spacing(8),
    backgroundColor: theme.palette.background.default,
    zIndex: 1,
  },
  selectAllButton: {
    marginLeft: theme.spacing(4),
  },
  rightToolbarWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
});

type DatatablePagination = {
  page: number;
  limit: number;
};

type DatatableData = object | string[] | number[];

type ShouldRefreshData = {
  value: boolean;
  reason: string | null;
};

type DatatableProps = {
  columns: MUIDataTableColumnDef[];
  pagination?: DatatablePagination;
  loading: boolean;
  total: number;
  data: DatatableData[];
  onSelectedChange?: (items: DatatableItemsData) => void;
  onTableChange: (tableState: MUIDataTableState) => void;
  onSortChange: (changedColumn: string, direction: string) => void;
  onApplyFiltersClick?: () => void;
  onApplySearchFiltersClick?: () => void;
  onCustomButtonClick?: () => void;
  onPopoverFiltersClick?: () => void;
  customButtonLabel?: string;
  isFiltered?: boolean;
  removeShadow?: boolean;
  disableSort?: boolean;
  toolbarData?: CustomToolbarItem[];
  shouldRefresh?: ShouldRefreshData;
  customTextLabels?: Record<string, string>;
  showFilters?: boolean;
  showSearchFilters?: boolean;
  setRowProps?: (row: any[], rowIndex: number) => object;
  title?: JSX.Element;
  showSelectionExtension?: boolean;
  onSelectAllClick?: () => void;
  selectedAll?: boolean;
  selectableRows?: boolean;
  notSelectableRows?: number[];
  selectableRowsHeader?: boolean;
  leftToolbar?: JSX.Element | Boolean;
  tableToolbar?: JSX.Element | Boolean;
  requestCounter?: JSX.Element;
  showPopoverFilters?: boolean;
  popoverFilters?: JSX.Element;
  filterChips?: JSX.Element;
  skipHeader?: boolean;
  rowsPerPageOptions?: Array<number>;
};

export type DatatableItemsData = {
  itemIds: Record<string, string>;
  count: number;
  items: Record<string, string | any[]>;
};

const Datatable: FC<DatatableProps> = ({
  loading = false,
  columns,
  pagination = {
    page: 1,
    limit: 25,
  },
  total = 0,
  data,
  onSelectedChange,
  onTableChange,
  onSortChange,
  onApplyFiltersClick,
  onApplySearchFiltersClick,
  onCustomButtonClick,
  customButtonLabel,
  isFiltered = false,
  removeShadow = false,
  disableSort = false,
  toolbarData,
  shouldRefresh = { value: false, reason: null },
  customTextLabels,
  showFilters = true,
  showSearchFilters = false,
  setRowProps,
  title,
  showSelectionExtension = false,
  onSelectAllClick = () => null,
  selectedAll = false,
  selectableRows = true,
  notSelectableRows,
  selectableRowsHeader = true,
  leftToolbar,
  tableToolbar,
  requestCounter,
  popoverFilters,
  filterChips,
  skipHeader = false,
  rowsPerPageOptions = [25, 50, 100, 200],
}) => {
  const { t } = useTranslation();
  const classes = useStyle();

  const isRowSelectable = (params: number) =>
    notSelectableRows ? notSelectableRows.indexOf(params) === -1 : true;

  const selected = useRef<DatatableItemsData>({ itemIds: {}, count: 0, items: {} });

  const getTableChanges = (action: string) => ['changeRowsPerPage', 'changePage'].includes(action);

  const setSelected = (newSelected: DatatableItemsData) => {
    selected.current = newSelected;
    if (onSelectedChange) onSelectedChange(selected.current);
  };

  const resetSelected = () => {
    setSelected({ itemIds: {}, count: 0, items: {} });
  };

  const selectAllItems = () => {
    const newSelectedItemsIds: Record<string, string> = {};
    const newSelectedItems: Record<string, string | any[]> = {};
    data.forEach((_, index) => {
      const itemData = data[index];
      if (Array.isArray(itemData)) {
        const itemId = (itemData as any[])[0];
        newSelectedItemsIds[itemId] = itemId;
        newSelectedItems[itemId] = itemData;
      }
    });
    setSelected({ itemIds: newSelectedItemsIds, count: data.length, items: newSelectedItems });
  };

  useEffect(() => {
    if (
      shouldRefresh.value &&
      shouldRefresh.reason &&
      [
        refreshReasons.DELETED,
        refreshReasons.FILTERS_APPLIED,
        refreshReasons.UPDATED,
        refreshReasons.MOVED,
      ].includes(shouldRefresh.reason)
    ) {
      resetSelected();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefresh]);

  useEffect(() => {
    if (data.length && selectedAll) {
      selectAllItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const getMuiTheme = () => ({
    components: {
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&$selected': {
              backgroundColor: `${theme.palette.custom.lightBlue} !important`,
            },
          },
        },
      },
      MUIDataTable: {
        styleOverrides: {
          paper: {
            boxShadow: removeShadow && 'none',
          },
        },
      },
      MUIDataTableToolbar: {
        styleOverrides: {
          actions: {
            display: 'flex',
            flexDirection: 'row-reverse',
            alignItems: 'center',
          },
        },
      },
      MUIDataTableToolbarSelect: {
        styleOverrides: {
          root: {
            height: 64,
          },
          title: {
            paddingLeft: 0,
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          iconDirectionDesc: {
            marginTop: 10,
            transform: 'rotate(180deg)',
          },
          iconDirectionAsc: {
            marginTop: 10,
            transform: 'rotate(0)',
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          hintIconWithSortIcon: {
            marginTop: 3,
            fontSize: 16,
            marginLeft: 0,
            color: theme.palette.primary.main,
          },
          fixedHeader: {
            zIndex: 1,
          },
          hintIconAlone: {
            marginTop: 3,
            fontSize: 16,
            marginLeft: 8,
            color: theme.palette.primary.main,
          },
        },
      },
      MUIDataTableSelectCell: {
        styleOverrides: {
          headerCell: {
            zIndex: 1,
          },
          fixedLeft: {
            zIndex: 1,
          },
        },
      },
    },
  });

  // MUI Datatables library doesn't allow objects passing as columns data so we have to rely on arrays.
  // Sometimes data providers (entities, instruments, portfolioss, etc.) put single id element in a column, sometimes
  // they put it in an array along with other necessary properties, so we want to ensure that they are arrays here,
  // just for unification.
  const getId = ([element]: any[]): string => castArray(element)[0];

  const toggleElement = (element: any[]) => {
    const id = getId(element);

    const { current } = selected;
    if (current.itemIds[id]) {
      delete current.itemIds[id];
      current.count -= 1;
      delete current.items[id];
    } else {
      current.itemIds[id] = id as string;
      current.count += 1;
      current.items[id] = element;
    }
    setSelected(current);
  };

  const onRowSelectionChange = (currentRowsSelected: MUIDataTableBodyCell[]) => {
    if (currentRowsSelected.length === 0) {
      resetSelected();
    } else {
      currentRowsSelected.forEach(({ dataIndex }) => {
        if (dataIndex !== undefined && Array.isArray(data[dataIndex])) {
          toggleElement(data[dataIndex] as any[]);
        }
      });
    }
  };

  const rowsSelected = data
    .map((element, index) => {
      return (
        (selectedAll && Array.isArray(element)
          ? true
          : selected.current.itemIds[getId(element as any[])]) && index
      );
    })
    .filter((item) => typeof item === 'number');

  const selectElementLabel =
    selected.current.count > 0 &&
    `${selectedAll ? total : selected.current.count} ${t('row(s) selected')}`;

  const tableTitle = selectElementLabel || title;

  const options: MUIDataTableOptions = {
    page: pagination.page || 0,
    rowsPerPage: pagination.limit || 25,
    rowsPerPageOptions,
    serverSide: true,
    print: false,
    download: false,
    filter: false,
    search: false,
    textLabels: customTextLabels,
    selectableRowsHeader,
    onRowsDelete: undefined,
    rowsSelected,
    onRowSelectionChange,
    selectToolbarPlacement: 'none',
    onColumnSortChange: onSortChange,
    onTableChange: (action, tableState) => getTableChanges(action) && onTableChange(tableState),
    viewColumns: false, // TODO: Enable when user profile feature allows to remember selected settings
    count: total,
    ...(disableSort && { sort: false }),
    ...(setRowProps && { setRowProps }),
    selectableRows: selectableRows ? 'multiple' : 'none',
    isRowSelectable,
  };

  const selectionExtension =
    showSelectionExtension && !selectedAll && selected.current.count === pagination.limit ? (
      <Button
        variant="text"
        size="small"
        onClick={onSelectAllClick}
        className={classes.selectAllButton}
      >
        {t('SELECT_ALL_ITEMS').replace('{0}', String(total))}
      </Button>
    ) : null;

  return (
    <div className={classes.container}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={getMuiTheme()}>
          {loading && <Loader cover />}
          <div
            className={leftToolbar ? classes.toolbarWrapperWithLeftMenu : classes.toolbarWrapper}
          >
            <div>{!loading && leftToolbar ? leftToolbar : ''}</div>
            <div className={classes.rightToolbarWrapper}>
              {!loading && requestCounter ? requestCounter : ''}
              <CustomToolbar
                menuItems={toolbarData}
                onApplyFiltersClick={onApplyFiltersClick}
                onApplySearchFiltersClick={onApplySearchFiltersClick}
                isFiltered={isFiltered}
                showFilters={showFilters}
                showSearchFilters={showSearchFilters}
                onCustomButtonClick={onCustomButtonClick}
                customButtonLabel={customButtonLabel}
                popoverFilters={popoverFilters}
              />
            </div>
          </div>
          <DatatableStyledWrapper>
            <MUIDataTable
              title={
                !loading && !selectElementLabel && title
                  ? tableTitle
                  : !skipHeader && (
                      <Box>
                        {!loading && popoverFilters && filterChips}
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1">
                            {loading ? 'Loading...' : selectElementLabel}
                          </Typography>
                          {!loading && selectElementLabel ? selectionExtension : null}
                        </Box>
                        {tableToolbar && <Box display="flex">{tableToolbar}</Box>}
                      </Box>
                    )
              }
              data={data}
              columns={columns}
              options={options}
            />
          </DatatableStyledWrapper>
        </ThemeProvider>
      </StyledEngineProvider>
    </div>
  );
};

export default Datatable;
